"use client";

import { getDiscountToDisplay } from "@components/commerce/helpers";
import { CalloutDiscount } from "@components/commerce/product/callout-discount/CalloutDiscount";
import ProductBuyCardImages from "@components/commerce/product/right-panel/buy-card/images/ProductBuyCardImages";
import { ProductBuyCardTitleAndPrice } from "@components/commerce/product/right-panel/buy-card/title-and-price/ProductBuyCardTitleAndPrice";
import ProductBuyCardSizes from "@components/commerce/product/right-panel/buy-card/sizes/ProductBuyCardSizes";
import {
    getProductImageThumbnails,
    isAnyVariantsInStock,
} from "@components/commerce/product/right-panel/buy-card/utils";
import { ProductBadges } from "@components/ui/product-badges/ProductBadges";
import type { ProductPrice } from "@graphql/generated/types";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Attributes, CustomAttributes } from "@lib/enums/ProductAttributes";
import { useFeatureFlag } from "@lib/feature-flags/FeatureFlagsContext";
import type { Product, ProductVariant } from "@lib/product/types";
import type { RecommendationResult } from "@lib/algolia/types";
import { getProductAttributeValue } from "@lib/utils/attributesUtils";
import { hasNotifySizeAvailability } from "@lib/utils/notificationFeature";
import type { EccoClubBadge, SaleBadge } from "@lib/wrappers/algolia/types";
import { useStore } from "@store/store";
import { Text } from "@ecco/ui/text";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { useAuth } from "@lib/auth/useAuth";
import { useLocale } from "next-intl";
import dynamic from "next/dynamic";
import React, { type MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import { space } from "@ecco/ui/tokens";
import { PromoDescription } from "./promo-description/PromoDescription";
import { EccoClubMemberDiscount } from "./ecco-club-member-discount/EccoClubMemberDiscount";
import { styles } from "./styles";
import type { PDPProductThumbnail } from "./types";
import { eccoClubVerticalGradient } from "@ecco/ui/tokens/colors";
import { useProductAccess } from "@hooks/useProductAccess";
import { useVariantsAvailability } from "@hooks/useCartItemsAvailability";
import { getCustomField } from "@lib/utils/cartProductUtility";
import ProductBuyCardSizesVB from "./sizes/ProductBuyCardSizesVB";
import { getActiveCustomBadges } from "@components/ui/product-badges/utils";
import { RUM_ACTION_NAME } from "@lib/monitoring/rumActionNames";

const ClickAndCollectCard = dynamic(() => import("./click-and-collect/ClickAndCollectCard"), {
    ssr: false,
});

const ClickAndCollectCardVB = dynamic(() => import("./click-and-collect/ClickAndCollectCardVB"), {
    ssr: false,
});
const AddedToBagModal = dynamic(
    () => import("@components/ui/modals/add-to-bag-modal/AddedToBagModal")
);
const TryAndBuy = dynamic(() => import("@components/try-and-buy"));
const JoinTheClub = dynamic(() => import("@components/join-the-club"));
const NotifySizeAvailability = dynamic(
    () => import("@components/commerce/product/notify-size-availability")
);

interface ProductBuyCardProps {
    product: Product;
    initialProductPrice?: ProductPrice;
    onAddToCart: (selectedVariant?: ProductVariant) => Promise<void>;
    calloutDiscountTitle?: string;
    calloutDiscountDescription?: string;
    imageThumbnails: PDPProductThumbnail[];
    productBuyCardRef?: (node?: Element) => void;
    headlineProductNameRef?: MutableRefObject<HTMLHeadingElement>;
    sizeContainerRef?(node?: Element): void;
    isFootwear?: boolean;
    saleBadge?: SaleBadge;
    eccoClubBadge?: EccoClubBadge;
    isFetchingProduct?: boolean;
    recommendations?: RecommendationResult[];
}

export const ProductBuyCard = React.memo(
    ({
        product,
        initialProductPrice,
        onAddToCart,
        calloutDiscountTitle,
        calloutDiscountDescription,
        imageThumbnails,
        headlineProductNameRef,
        productBuyCardRef,
        sizeContainerRef,
        isFootwear = false,
        saleBadge = null,
        eccoClubBadge,
        isFetchingProduct = false,
        recommendations = [],
    }: ProductBuyCardProps) => {
        const activeProduct = useStore((state) => state.data.pdpState.activeProduct);
        const { variant: selectedVariant, isAvailable } = activeProduct || {};
        const setActiveProduct = useStore((state) => state.setActiveProduct);
        const {
            outOfStockSkus,
            isChecked: isAvailabilityChecked,
            isCartError,
            retryCart,
        } = useVariantsAvailability(product.variants);
        const isWaitingForAvailability =
            Boolean(selectedVariant) && !isAvailabilityChecked && !isCartError;
        const isSelectedVariantOutOfStock =
            Boolean(selectedVariant?.sku) && outOfStockSkus.includes(selectedVariant.sku);

        // A size can be selected before the availability check resolves (URL preselect, one-size).
        // Remember which SKU we marked unavailable so it can be restored if stock recovers.
        const outOfStockOverrideSkuRef = useRef<string | null>(null);
        useEffect(() => {
            const selectedSku = selectedVariant?.sku;
            if (outOfStockOverrideSkuRef.current !== selectedSku) {
                outOfStockOverrideSkuRef.current = null;
            }
            if (!activeProduct || !selectedSku) return;

            if (isSelectedVariantOutOfStock) {
                if (isAvailable) {
                    outOfStockOverrideSkuRef.current = selectedSku;
                    setActiveProduct({ ...activeProduct, isAvailable: false });
                }
                return;
            }

            if (!isAvailable && outOfStockOverrideSkuRef.current === selectedSku) {
                outOfStockOverrideSkuRef.current = null;
                setActiveProduct({ ...activeProduct, isAvailable: true });
            }
        }, [
            activeProduct,
            selectedVariant?.sku,
            isAvailable,
            isSelectedVariantOutOfStock,
            setActiveProduct,
        ]);
        const isOpenAddedToBagModal = useStore(
            (state) => state.data.pdpState.isOpenAddedToBagModal
        );
        const setIsOpenAddedToBagModal = useStore((state) => state.setIsOpenAddedToBagModal);
        const price = selectedVariant ? selectedVariant?.price : initialProductPrice;
        const locale = useLocale();
        const { status } = useAuth();
        const [lastUsedProdId, setLastUsedProdId] = useState<string>(product.id);
        const [showNotifyModal, setShowNotifyModal] = useState(false);
        const addToBagButtonRef = useRef<HTMLButtonElement>(null);
        const [showSelectSizeError, setShowSelectSizeError] = useState(false);
        const isOmnichannelMarket = useFeatureFlag("isOmnichannelMarket");
        const { getMultiple } = useMicrocopy();
        const getCart = useStore((state) => state.getCart);
        const offerIds: string[] = getCustomField(
            product.variants[0]?.price,
            CustomAttributes.OFFER_IDS
        );

        const currentCart = getCart();

        useEffect(() => {
            if (showSelectSizeError) {
                const timeout = setTimeout(() => {
                    setShowSelectSizeError(false);
                }, 3000);
                return () => clearTimeout(timeout);
            }
        }, [showSelectSizeError]);

        const microcopies = getMultiple(CommonCMS.page, [
            CommonCMS.PDP.soldOutTitle,
            CommonCMS.PDP.soldOutSubtitle,
            CommonCMS.PDP.addToCart,
            CommonCMS.PDP.pleaseSelectSize,
            CommonCMS.PDP.notifyMe,
        ]);

        const { attributes, variants, sku } = product;

        const { showEccoClubCTA, showAddToCartButton, isAccessUncertain, hasEccoClub } =
            useProductAccess(product);

        const sizeSuggestion = product.productAttributes.find(
            (attr) => attr.name === "SizeSuggestionProduct"
        )?.value?.[locale];

        const hasTryAndBuy = useFeatureFlag("hasTryAndBuy");
        // Mount fit feedback whenever product has data; Wingify CSS shows/hides the labels.
        const fitFeedbackSelectedSize = Number(sizeSuggestion);
        const fitFeedback =
            sizeSuggestion != null &&
            sizeSuggestion !== "" &&
            !Number.isNaN(fitFeedbackSelectedSize)
                ? { selectedSize: fitFeedbackSelectedSize }
                : undefined;

        const { color, productImageThumbnails } = useMemo(() => {
            const currentThumbnail = getProductImageThumbnails(product, locale);

            // Ensure imageThumbnails is always a valid array before spreading
            const thumbnailsArray = Array.isArray(imageThumbnails) ? imageThumbnails : [];

            const alreadyExists = thumbnailsArray.some(
                (t) => t.colorCode === currentThumbnail.colorCode
            );

            return {
                color: getProductAttributeValue(attributes, Attributes.dominantColor, locale),
                productImageThumbnails: alreadyExists
                    ? thumbnailsArray
                    : [...thumbnailsArray, currentThumbnail],
            };
        }, [product, locale, attributes, imageThumbnails]);

        const isAnyProductVariantInStock = isAnyVariantsInStock(variants);

        const {
            discountPercentage,
            discountPercentagePreviousLowest,
            previousLowestPrice,
            recommendedPrice,
        } = useMemo(
            () => ({
                discountPercentage:
                    Number(
                        getProductAttributeValue(
                            price?.custom?.customFieldsRaw,
                            CustomAttributes.DISCOUNT_PERCENTAGE
                        )
                    ) || 0,
                discountPercentagePreviousLowest:
                    Number(
                        getProductAttributeValue(
                            price?.custom?.customFieldsRaw,
                            CustomAttributes.DISCOUNT_PERCENTAGE_PREVIOUS_LOWEST_PRICE
                        )
                    ) || 0,
                previousLowestPrice: getProductAttributeValue(
                    price?.custom?.customFieldsRaw,
                    CustomAttributes.PREVIOUS_LOWEST_PRICE
                ),
                recommendedPrice: getProductAttributeValue(
                    price?.custom?.customFieldsRaw,
                    CustomAttributes.RECOMMENDED_PRICE
                ),
            }),
            [price?.custom?.customFieldsRaw]
        );

        const customBadges = getActiveCustomBadges([
            getProductAttributeValue(
                product.productAttributes,
                Attributes.CustomBadge1Scheduled,
                locale
            ),
            getProductAttributeValue(
                product.productAttributes,
                Attributes.CustomBadge2Scheduled,
                locale
            ),
            getProductAttributeValue(
                product.productAttributes,
                Attributes.CustomBadge3Scheduled,
                locale
            ),
        ]);
        const isDiscounted = discountPercentage > 0;
        const discountToDisplay = getDiscountToDisplay({
            isDiscounted,
            currentPrice: price?.value?.centAmount,
            previousLowestPrice: previousLowestPrice?.centAmount,
            recommendedPrice: recommendedPrice?.centAmount,
            discountPercentage,
            discountPercentagePreviousLowest,
        });

        if (lastUsedProdId !== product.id) {
            setLastUsedProdId(product.id);
            setShowSelectSizeError(false);
        }

        const onAddToCartClick = () => {
            if (!selectedVariant) {
                setShowSelectSizeError(true);
                return;
            }
            if (!isAvailabilityChecked) {
                if (isCartError) {
                    retryCart();
                }
                return;
            }
            if (!isAvailable || isSelectedVariantOutOfStock) {
                if (hasNotifySizeAvailability) {
                    setShowNotifyModal(true);
                }
                return;
            }
            handleAddToCart();
        };

        const isNotifyMeAction = Boolean(
            selectedVariant && !isAvailable && hasNotifySizeAvailability
        );

        const getButtonText = () => {
            if (isNotifyMeAction) {
                return microcopies[CommonCMS.PDP.notifyMe];
            }

            return microcopies[CommonCMS.PDP.addToCart];
        };

        function handleAddToCart(): Promise<void> {
            if (!selectedVariant) {
                setShowSelectSizeError(true);
                return;
            }
            setIsOpenAddedToBagModal(true);
            onAddToCart(selectedVariant);
        }

        function handleCloseBasketModal() {
            setIsOpenAddedToBagModal(false);
        }

        const descriptiveHeading = getProductAttributeValue(
            attributes,
            Attributes.descriptiveHeading,
            locale
        );

        return (
            <Box sx={styles.container} data-testid="product-buy-card">
                <Box display={["block"]} mt={4}>
                    <Box display={{ base: "block", lg: "none" }}>
                        <ProductBuyCardImages
                            productImageThumbnails={productImageThumbnails}
                            productColorLabel={color}
                            isFetchingProduct={isFetchingProduct}
                        />
                    </Box>
                    {(isDiscounted || customBadges.length > 0 || saleBadge || eccoClubBadge) && (
                        <ProductBadges
                            discount={discountToDisplay > 0 && `-${discountToDisplay}%`}
                            customBadges={customBadges}
                            isOnProductTile={false}
                            saleBadge={saleBadge}
                            eccoClubBadge={eccoClubBadge}
                        />
                    )}

                    {hasEccoClub && (
                        <Text
                            as={"span"}
                            fontSize={"12px"}
                            fontStyle={"normal"}
                            fontWeight="700"
                            background={eccoClubVerticalGradient}
                            backgroundClip="text"
                            color="transparent"
                        >
                            {"ECCO CLUB · EARLY ACCESS"}
                        </Text>
                    )}

                    <Flex sx={styles.variantTitleWishlist}>
                        <ProductBuyCardTitleAndPrice
                            heading={descriptiveHeading}
                            price={price}
                            product={product}
                        />
                    </Flex>
                    <EccoClubMemberDiscount badge={eccoClubBadge} variant="pdp" />
                </Box>
                {showEccoClubCTA && <JoinTheClub />}
                {/* /We need this element as an anchor for track visibility. There are some desktop screens or
                product names that causes the sticky to be visible above CTA button. This is a workaround to fix that.
            */}
                <Box display={["none", "block"]} visibility={"hidden"} ref={sizeContainerRef} />
                <Box display={{ base: "none", lg: "block" }}>
                    <ProductBuyCardImages
                        productImageThumbnails={productImageThumbnails}
                        productColorLabel={color}
                        isFetchingProduct={isFetchingProduct}
                    />
                </Box>
                {offerIds?.length && <PromoDescription offerIds={offerIds} />}
                {calloutDiscountTitle && (
                    <CalloutDiscount
                        title={calloutDiscountTitle}
                        description={calloutDiscountDescription}
                    />
                )}

                {isAnyProductVariantInStock ? (
                    <Box onClick={() => setShowSelectSizeError(false)} ref={headlineProductNameRef}>
                        <ProductBuyCardSizes
                            variants={variants}
                            attributes={attributes}
                            productName={product.name}
                            marginBottom={fitFeedback ? 2 : 6}
                            isHighlighted={showSelectSizeError}
                            fitFeedback={fitFeedback}
                            recommendations={recommendations}
                        />
                        <ProductBuyCardSizesVB
                            product={product}
                            variants={variants}
                            attributes={attributes}
                            productName={product.name}
                            marginBottom={fitFeedback ? 2 : 6}
                            isHighlighted={showSelectSizeError}
                            recommendations={recommendations}
                        />
                    </Box>
                ) : (
                    <Box>
                        <Text
                            mb={2}
                            fontWeight={"bold"}
                            fontSize={["mobileBodyTextLarge", "desktopBodyTextLarge"]}
                        >
                            {microcopies[CommonCMS.PDP.soldOutTitle]}
                        </Text>
                        <Text
                            color={"gray.500"}
                            fontSize={["mobileBodyTextSmall", "desktopBodyTextSmall"]}
                        >
                            {microcopies[CommonCMS.PDP.soldOutSubtitle]}
                        </Text>
                    </Box>
                )}

                {!selectedVariant && showSelectSizeError && (
                    <Box mb={2}>
                        <Text
                            color="red.500"
                            fontWeight={"bold"}
                            fontSize={["mobileBodyTextMedium", "desktopBodyTextMedium"]}
                        >
                            {microcopies[CommonCMS.PDP.pleaseSelectSize]}
                        </Text>
                    </Box>
                )}

                {showAddToCartButton && (
                    <Flex gap={space["0.2"]} w={"100%"}>
                        {isAnyProductVariantInStock && (
                            <Box sx={styles.submitContainer} ref={productBuyCardRef}>
                                <Button
                                    ref={addToBagButtonRef}
                                    disabled={status !== "authenticated" || isAccessUncertain}
                                    isLoading={isAccessUncertain || isWaitingForAvailability}
                                    colorScheme="black"
                                    sx={styles.submitButton}
                                    size="md"
                                    fontSize={"desktopBodyB2"}
                                    onClick={onAddToCartClick}
                                    data-testid="add-to-cart-button"
                                    data-dd-action-name={
                                        isNotifyMeAction
                                            ? RUM_ACTION_NAME.NOTIFY_ME
                                            : RUM_ACTION_NAME.ADD_TO_CART
                                    }
                                >
                                    <span>{getButtonText()}</span>
                                </Button>
                            </Box>
                        )}
                    </Flex>
                )}
                {/* Not all the markets will have Omnichannel. We check for that to render the component*/}
                {/* We render the box no matter what, otherwise will create layout shift in certain screen sizes until we check isAnyProductVariantInStock*/}
                <Box pr={0} w={"100%"}>
                    {isOmnichannelMarket && isAnyProductVariantInStock && (
                        <ClickAndCollectCard
                            product={product}
                            onClickAndCollectClick={() => {
                                setShowSelectSizeError(true);
                            }}
                        />
                    )}
                </Box>
                {hasTryAndBuy && (
                    <Box pr={0} w={"100%"} data-testid="try-and-buy-container">
                        <TryAndBuy
                            currentCart={currentCart}
                            selectedVariant={selectedVariant}
                            product={product}
                        />
                    </Box>
                )}

                <Box pr={0} w={"100%"}>
                    {isOmnichannelMarket && isAnyProductVariantInStock && (
                        <ClickAndCollectCardVB
                            product={product}
                            onClickAndCollectClick={() => {
                                setShowSelectSizeError(true);
                            }}
                        />
                    )}
                </Box>

                {hasNotifySizeAvailability && showNotifyModal && selectedVariant && (
                    <NotifySizeAvailability
                        isOpen={showNotifyModal}
                        onClose={() => setShowNotifyModal(false)}
                        selectedSize={getProductAttributeValue(
                            selectedVariant.attributesRaw,
                            Attributes.size
                        )}
                        selectedProductSku={selectedVariant.sku}
                        productName={product.name}
                        recommendations={recommendations}
                    />
                )}
                {isOpenAddedToBagModal && (
                    <AddedToBagModal
                        isOpen={isOpenAddedToBagModal}
                        onClose={handleCloseBasketModal}
                        currentProductKey={sku}
                        isFootwear={isFootwear}
                        triggerRef={addToBagButtonRef}
                    />
                )}
            </Box>
        );
    }
);

ProductBuyCard.displayName = "ProductBuyCard";

export default ProductBuyCard;
